import { fromCodeSuggestDataList } from './CodeSuggestData.js';
import { fromConsultationDetailsData } from './ConsultationDetailsData.js';
import { ConsultationStatus } from './ConsultationStatus.js';
import { fromContentProcessingErrorData } from './ContentProcessingErrorData.js';
import { fromExistingPatientCodesData } from './ExistingPatientCodesData.js';
import { fromMeasurementDataList } from './MeasurementData.js';
import { fromOutcomeTaskDataList } from './OutcomeTaskData.js';
import { fromPotentialExistingCodeDataList } from './PotentialExistingCodeData.js';
import { fromTranscriptSegmentDataList } from './TranscriptSegmentData.js';
import {
  asBoolean,
  assignIfPresent,
  mapStringList,
  optionalEnum,
  optionalNumber,
  optionalString,
  requireNumber,
  requireObject,
  requireString,
} from './parse.js';

const TYPE_NAME = 'PatientConsultationData';

/**
 * Mirrors com.medprompt.generator.consultation.notes.NotesMode
 */
export const NotesMode = Object.freeze({
  BRIEF: 'BRIEF',
  MEDIUM: 'MEDIUM',
  DETAILED: 'DETAILED',
});

const optionalObject = (mapper) => (value) => (value == null ? null : mapper(value));

function fromStringMap(raw, typeName) {
  const source = requireObject(raw, typeName);
  const result = {};
  Object.entries(source).forEach(([key, value]) => {
    if (typeof value !== 'string') {
      throw new Error(`${typeName}: expected a string for "${key}"`);
    }
    result[key] = value;
  });
  return result;
}

/** Keyed by ConsultationContentType name. */
function fromContentTypeToError(raw) {
  const source = requireObject(raw, `${TYPE_NAME}.contentTypeToError`);
  const errors = {};
  Object.entries(source).forEach(([contentType, error]) => {
    errors[contentType] = fromContentProcessingErrorData(error);
  });
  return errors;
}

/**
 * Mirrors com.medprompt.consultation.PatientConsultationData (`@JsonInclude(NON_NULL)`).
 * Optional fields Jackson left out stay absent; see `assignIfPresent`.
 *
 * @typedef {object} PatientConsultationData
 * @property {string} consultationId
 * @property {number} timestamp
 * @property {number|null} speakersCount
 * @property {boolean} isRecording
 * @property {boolean} isInvalid
 * @property {boolean} isFiledAway
 * @property {string|null} [nhsId]
 * @property {string|null} [patientId]
 * @property {import('./TranscriptSegmentData.js').TranscriptSegmentData[]} [transcriptSegments]
 * @property {Object<string, string>|null} [transcriptSpeakers]
 * @property {string|null} [notes]
 * @property {string|null} [summary]
 * @property {import('./ExistingPatientCodesData.js').ExistingPatientCodesData|null} [existingCodes]
 * @property {import('./CodeSuggestData.js').CodeSuggestData[]} [newCodes]
 * @property {import('./PotentialExistingCodeData.js').PotentialExistingCodeData[]} [potentialExistingCodes]
 * @property {import('./MeasurementData.js').MeasurementData[]} [measurements]
 * @property {import('./OutcomeTaskData.js').OutcomeTaskData[]} [outcomeTasks]
 * @property {Object<string, import('./ContentProcessingErrorData.js').ContentProcessingErrorData>} [contentTypeToError]
 * @property {string|null} [templateId]
 * @property {string|null} [notesMode]
 * @property {string[]} [commIds]
 * @property {import('./ConsultationDetailsData.js').ConsultationDetailsData|null} [consultationDetails]
 * @property {string|null} [userId]
 * @property {string|null} [status]
 */

/**
 * @param {object} raw
 * @returns {PatientConsultationData}
 */
export function fromPatientConsultationData(raw) {
  const source = requireObject(raw, TYPE_NAME);
  const consultation = {
    consultationId: requireString(source, 'consultationId', TYPE_NAME),
    timestamp: requireNumber(source, 'timestamp', TYPE_NAME),
    speakersCount: optionalNumber(source.speakersCount),
    isRecording: asBoolean(source.isRecording),
    isInvalid: asBoolean(source.isInvalid),
    isFiledAway: asBoolean(source.isFiledAway),
  };

  assignIfPresent(consultation, source, 'nhsId', optionalString);
  assignIfPresent(consultation, source, 'patientId', optionalString);
  assignIfPresent(consultation, source, 'transcriptSegments', fromTranscriptSegmentDataList);
  assignIfPresent(consultation, source, 'transcriptSpeakers', optionalObject((value) => (
    fromStringMap(value, `${TYPE_NAME}.transcriptSpeakers`)
  )));
  assignIfPresent(consultation, source, 'notes', optionalString);
  assignIfPresent(consultation, source, 'summary', optionalString);
  assignIfPresent(consultation, source, 'existingCodes', optionalObject(fromExistingPatientCodesData));
  assignIfPresent(consultation, source, 'newCodes', fromCodeSuggestDataList);
  assignIfPresent(consultation, source, 'potentialExistingCodes', fromPotentialExistingCodeDataList);
  assignIfPresent(consultation, source, 'measurements', fromMeasurementDataList);
  assignIfPresent(consultation, source, 'outcomeTasks', fromOutcomeTaskDataList);
  assignIfPresent(consultation, source, 'contentTypeToError', optionalObject(fromContentTypeToError));
  assignIfPresent(consultation, source, 'templateId', optionalString);
  assignIfPresent(consultation, source, 'notesMode', (value) => (
    optionalEnum(value, NotesMode, TYPE_NAME, 'notesMode')
  ));
  assignIfPresent(consultation, source, 'commIds', (value) => mapStringList(value, `${TYPE_NAME}.commIds`));
  assignIfPresent(consultation, source, 'consultationDetails', optionalObject(fromConsultationDetailsData));
  assignIfPresent(consultation, source, 'userId', optionalString);
  assignIfPresent(consultation, source, 'status', (value) => (
    optionalEnum(value, ConsultationStatus, TYPE_NAME, 'status')
  ));

  return consultation;
}
